import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/api';
import { toast } from 'react-toastify';

const AdminSiteVisibility: React.FC = () => {
    const { token } = useAuth();
    
    // Maintenance Mode States
    const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');
    const [bannerImage, setBannerImage] = useState('');
    const [mobileBannerImage, setMobileBannerImage] = useState('');
    const [siteLogo, setSiteLogo] = useState('');
    const [whiteLogo, setWhiteLogo] = useState('');
    const [showDeveloperLeads, setShowDeveloperLeads] = useState(false);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await api.getSystemSettings();
                if (data && !data.message) {
                    setIsMaintenanceMode(data.isMaintenanceMode || false);
                    setMaintenanceMessage(data.maintenanceMessage || 'Our website is currently undergoing scheduled maintenance. We will be back shortly!');
                    setBannerImage(data.bannerImage || '');
                    setMobileBannerImage(data.mobileBannerImage || '');
                    setSiteLogo(data.siteLogo || '');
                    setWhiteLogo(data.whiteLogo || '');
                    setShowDeveloperLeads(data.showDeveloperLeads || false);
                }
            } catch (err) {
                console.error('Failed to fetch system settings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'desktop' | 'mobile' | 'logo' | 'whiteLogo') => {
        const file = e.target.files?.[0];
        if (!file || !token) return;

        const formData = new FormData();
        formData.append('images', file);

        try {
            const res = await fetch(`${(window as any).API_BASE || 'https://mypropertyguru.in/api'}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (data.urls && data.urls.length > 0) {
                if (field === 'desktop') setBannerImage(data.urls[0]);
                else if (field === 'mobile') setMobileBannerImage(data.urls[0]);
                else if (field === 'logo') setSiteLogo(data.urls[0]);
                else if (field === 'whiteLogo') setWhiteLogo(data.urls[0]);
                toast.success('Asset uploaded successfully!');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error('Failed to upload image');
        }
    };

    const handleSave = async () => {
        if (!token) return;
        setSaving(true);
        try {
            const data = await api.updateSystemSettings(token, {
                isMaintenanceMode,
                maintenanceMessage,
                bannerImage,
                mobileBannerImage,
                siteLogo,
                whiteLogo,
                showDeveloperLeads
            });
            if (data && !data.message) {
                toast.success('Site visibility settings updated successfully');
            } else {
                toast.error(data.message || 'Failed to update settings');
            }
        } catch (err) {
            console.error('Failed to update system settings:', err);
            toast.error('An error occurred while saving settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white leading-none">
                        Site Visibility <span className="text-[#05144b]">& Maintenance</span>
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-sm mt-3">
                        Control public access and manage scheduled downtime windows.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                    <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-slate-400 font-medium text-sm">Synchronizing Active Assets...</p>
                </div>
            ) : (
                <div className="max-w-4xl mx-auto space-y-8 mb-20">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 md:p-12 border border-slate-100 dark:border-slate-800 shadow-2xl shadow-blue-900/5 relative overflow-hidden">
                        <div className="relative z-10">
                            <div className="grid grid-cols-1 gap-10">
                                {/* Maintenance Toggle Card */}
                                <div className={`p-8 rounded-2xl border-2 transition-all duration-500 ${isMaintenanceMode ? 'bg-amber-50/50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900/50 shadow-lg shadow-amber-500/5' : 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700'}`}>
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className={`p-2.5 rounded-xl ${isMaintenanceMode ? 'bg-amber-100 text-amber-600' : 'bg-white border border-[#05144b] text-[#05144b]'}`}>
                                                    <i className={`fa-solid ${isMaintenanceMode ? 'fa-screwdriver-wrench' : 'fa-globe'} text-lg`}></i>
                                                </div>
                                                <h4 className="text-lg font-bold text-slate-900 dark:text-white">Maintenance Mode</h4>
                                            </div>
                                            <p className="text-sm font-medium text-slate-600 dark:text-slate-400 max-w-md">
                                                {isMaintenanceMode 
                                                    ? "The site is currently hidden from the public. Only administrators can access the frontend." 
                                                    : "The site is currently live. All users can browse and interact with the platform normally."}
                                            </p>
                                        </div>
                                        
                                        <div className="flex items-center gap-4">
                                            <span className={`text-xs font-bold ${isMaintenanceMode ? 'text-amber-600' : 'text-slate-400'}`}>
                                                {isMaintenanceMode ? 'DOWNTIME ACTIVE' : 'PUBLIC ACCESS'}
                                            </span>
                                            <button 
                                                onClick={() => setIsMaintenanceMode(!isMaintenanceMode)}
                                                className={`relative inline-flex h-9 w-16 items-center rounded-full transition-all duration-300 focus:outline-none ${isMaintenanceMode ? 'bg-amber-500 shadow-lg shadow-amber-500/20' : 'bg-slate-300'}`}
                                            >
                                                <span className={`inline-block h-7 w-7 transform rounded-full bg-white shadow-md transition-transform duration-300 ${isMaintenanceMode ? 'translate-x-8' : 'translate-x-1'}`} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Site Identity - Logo Management */}
                                <div className="p-8 rounded-2xl bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700">
                                    <div className="flex items-center gap-3 mb-8">
                                        <div className="p-2.5 rounded-xl bg-blue-50 text-[#05144b]">
                                            <i className="fa-solid fa-paintbrush text-lg"></i>
                                        </div>
                                        <h4 className="text-lg font-bold text-slate-900 dark:text-white">Site Identity</h4>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Standard Logo */}
                                        <div className="space-y-4">
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Primary Logo (Navbar)</label>
                                            <div className="relative group/logo">
                                                <div className="w-full h-32 rounded-2xl overflow-hidden bg-slate-50 dark:bg-slate-900/50 border-2 border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center transition-all group-hover/logo:border-[#05144b]/30 relative">
                                                    {siteLogo ? (
                                                        <>
                                                            <img src={getImageUrl(siteLogo)} alt="Site Logo" className="h-16 w-auto object-contain" />
                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/logo:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                                <label htmlFor="site-logo-upload" className="px-4 py-1.5 bg-white text-[#05144b] rounded-lg text-xs font-bold cursor-pointer hover:bg-blue-50 transition-all">Replace</label>
                                                                <button onClick={() => setSiteLogo('')} className="px-4 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 transition-all">Remove</button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="text-center">
                                                            <label htmlFor="site-logo-upload" className="px-5 py-2.5 bg-white border-2 border-[#05144b] text-[#05144b] rounded-xl text-xs font-bold cursor-pointer hover:bg-blue-50 transition-all shadow-md">Upload Logo</label>
                                                        </div>
                                                    )}
                                                    <input type="file" id="site-logo-upload" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'logo')} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* White Logo */}
                                        <div className="space-y-4">
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Light Logo (Footer)</label>
                                            <div className="relative group/white">
                                                <div className="w-full h-32 rounded-2xl overflow-hidden bg-slate-900 border-2 border-dashed border-slate-700 flex flex-col items-center justify-center transition-all group-hover/white:border-white/30 relative">
                                                    {whiteLogo ? (
                                                        <>
                                                            <img src={getImageUrl(whiteLogo)} alt="White Logo" className="h-16 w-auto object-contain" />
                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/white:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                                <label htmlFor="white-logo-upload" className="px-4 py-1.5 bg-white text-[#05144b] rounded-lg text-xs font-bold cursor-pointer hover:bg-blue-50 transition-all">Replace</label>
                                                                <button onClick={() => setWhiteLogo('')} className="px-4 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 transition-all">Remove</button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="text-center">
                                                            <label htmlFor="white-logo-upload" className="px-5 py-2.5 bg-slate-800 border-2 border-white/30 text-white rounded-xl text-xs font-bold cursor-pointer hover:bg-slate-700 transition-all shadow-md">Upload Light Logo</label>
                                                        </div>
                                                    )}
                                                    <input type="file" id="white-logo-upload" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'whiteLogo')} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                 {/* Maintenance Banner Image Card */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* Desktop Banner */}
                                    <div className="space-y-4">
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Desktop Splash Banner</label>
                                        <div className="relative group/desktop">
                                            <div className="w-full aspect-[16/10] rounded-2xl overflow-hidden bg-slate-50 dark:bg-slate-800/50 border-2 border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center transition-all group-hover/desktop:border-[#05144b]/30 relative">
                                                {bannerImage ? (
                                                    <>
                                                        <img 
                                                            src={getImageUrl(bannerImage)} 
                                                            alt="Maintenance Banner" 
                                                            className="w-full h-full object-cover" 
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/desktop:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                             <label htmlFor="desktop-image-upload" className="px-5 py-2 bg-white text-[#05144b] rounded-lg text-xs font-bold cursor-pointer hover:bg-blue-50 transition-all shadow-lg">
                                                                Replace
                                                            </label>
                                                            <button 
                                                                onClick={() => setBannerImage('')}
                                                                className="px-5 py-2 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 transition-all"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center p-6">
                                                        <i className="fa-solid fa-desktop text-2xl text-slate-300 dark:text-slate-600 mb-3 block"></i>
                                                        <label htmlFor="desktop-image-upload" className="px-5 py-2.5 bg-white border-2 border-[#05144b] text-[#05144b] rounded-xl text-xs font-bold cursor-pointer hover:bg-blue-50 transition-all shadow-md">
                                                            Upload Desktop
                                                        </label>
                                                    </div>
                                                )}
                                                <input 
                                                    type="file" 
                                                    id="desktop-image-upload" 
                                                    className="hidden" 
                                                    accept="image/*"
                                                    onChange={(e) => handleImageUpload(e, 'desktop')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Mobile Banner */}
                                    <div className="space-y-4">
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Mobile Splash Banner</label>
                                        <div className="relative group/mobile">
                                            <div className="w-full aspect-[9/16] max-w-[200px] mx-auto rounded-2xl overflow-hidden bg-slate-50 dark:bg-slate-800/50 border-2 border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center transition-all group-hover/mobile:border-[#05144b]/30 relative">
                                                {mobileBannerImage ? (
                                                    <>
                                                        <img 
                                                            src={getImageUrl(mobileBannerImage)} 
                                                            alt="Mobile Maintenance Banner" 
                                                            className="w-full h-full object-cover" 
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/mobile:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                             <label htmlFor="mobile-image-upload" className="px-4 py-2 bg-white text-[#05144b] rounded-lg text-xs font-bold cursor-pointer hover:bg-blue-50 transition-all shadow-lg">
                                                                Replace
                                                            </label>
                                                            <button 
                                                                onClick={() => setMobileBannerImage('')}
                                                                className="px-4 py-2 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 transition-all"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center p-6">
                                                        <i className="fa-solid fa-mobile-screen text-2xl text-slate-300 dark:text-slate-600 mb-3 block"></i>
                                                        <label htmlFor="mobile-image-upload" className="px-5 py-2.5 bg-white border-2 border-[#05144b] text-[#05144b] rounded-xl text-xs font-bold cursor-pointer hover:bg-blue-50 transition-all shadow-md">
                                                            Upload Mobile
                                                        </label>
                                                    </div>
                                                )}
                                                <input 
                                                    type="file" 
                                                    id="mobile-image-upload" 
                                                    className="hidden" 
                                                    accept="image/*"
                                                    onChange={(e) => handleImageUpload(e, 'mobile')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Save Button */}
                                <div className="pt-6">
                                    <button 
                                        onClick={handleSave}
                                        disabled={saving}
                                        className={`w-full py-5 rounded-xl text-sm font-bold tracking-wider transition-all transform active:scale-[0.99] flex items-center justify-center gap-3 ${saving ? 'bg-slate-400 cursor-not-allowed text-white' : 'bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 shadow-lg shadow-blue-900/5'}`}
                                    >
                                        {saving ? (
                                            <>
                                                 <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Saving Settings...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-check-double text-lg"></i>
                                                <span>Save Visibility Rules</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="text-center px-10">
                        <p className="text-slate-400 text-xs font-medium">
                            My Property Guru &copy; 2026 &bull; Admin Control Panel
                        </p>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminSiteVisibility;
